import React, { useState, Fragment, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    Chip,
    Stack,
    IconButton,
    Tooltip,
    Pagination,
    FormControl,
    Select,
    MenuItem,
    useMediaQuery,
    Grid,
    Paper,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import { deleteHedgingView } from '../../../redux/slices/operator';
import modifyScriptName from '../../../utils/modifyScriptName';

// Styled components matching the screenshot design
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    fontSize: '0.8rem',
    fontWeight: 500,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    verticalAlign: 'top',
    '&.header': {
        backgroundColor: '#f8f9fa',
        fontWeight: 600,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        fontSize: '0.85rem',
        textAlign: 'center',
    }
}));

const CompactCard = styled(Card)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
    boxShadow: 'none',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
}));

// Script card for individual instruments - with GPU acceleration to prevent shaking
const ScriptCard = styled(Box)(({ theme, scripttype }) => ({
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: scripttype === 'buy'
        ? alpha(theme.palette.success.main, 0.08)
        : alpha(theme.palette.error.main, 0.08),
    border: `1px solid ${scripttype === 'buy'
        ? alpha(theme.palette.success.main, 0.2)
        : alpha(theme.palette.error.main, 0.2)
        }`,
    minHeight: 80,
    position: 'relative',
    // GPU acceleration to prevent shaking during updates
    transform: 'translate3d(0, 0, 0)',
    willChange: 'auto',
    backfaceVisibility: 'hidden',
}));

// Details section styling
const DetailsSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#fafbfc',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderRadius: 8,
}));

// Analysis section styling - with GPU acceleration
const AnalysisSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: alpha(theme.palette.info.main, 0.04),
    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
    borderRadius: 8,
    // GPU acceleration to prevent shaking during updates
    transform: 'translate3d(0, 0, 0)',
    willChange: 'auto',
    backfaceVisibility: 'hidden',
}));

const mcxHedgingQuantityValue = [
    { name: 'GOLD', value: 100 },
    { name: 'GOLDM', value: 10 },
    { name: 'MGOLD', value: 10 },
    { name: 'SILVER', value: 30 },
    { name: 'SILVERM', value: 5 },
    { name: 'MSILVER', value: 5 },
    { name: 'CRUDEOIL', value: 100 },
    { name: 'NATURALGAS', value: 1250 },
    { name: 'COPPER', value: 2500 },
    { name: 'ZINC', value: 5000 },
    { name: 'LEAD', value: 5000 },
    { name: 'ALUMINIUM', value: 5000 },
];

const HedgingViewTable = React.memo(({
    data = [],
    pagination = {},
    loading = false,
    onPageChange,
    onPageSizeChange,
    currentPage = 1,
    pageSize = 10
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRowForDelete, setSelectedRowForDelete] = useState(null);

    // Expand/Collapse state for mobile cards
    const [expandedCards, setExpandedCards] = useState({});

    // Get real-time quotes data directly from Redux - NO THROTTLING for accuracy
    const quotesData = useSelector((state) => state?.trade?.quotesData || {});
    const previousQuote = useSelector((state) => state?.trade?.previousQuotesData || {});

    // Memoized quote data getter - direct real-time data
    const getQuoteData = useCallback((instrumentToken) => {
        if (!instrumentToken || !quotesData[instrumentToken]) {
            return { ltp: null, change: null, changePercent: null, bid: 0, ask: 0 };
        }

        const current = quotesData[instrumentToken];
        const previous = previousQuote[instrumentToken];

        const ltp = current.ltp || current.last_price || 0;
        const prevClose = previous?.close || current.ohlc?.close || ltp;
        const change = ltp - prevClose;
        const changePercent = prevClose !== 0 ? (change / prevClose) * 100 : 0;

        return {
            ltp,
            change,
            changePercent,
            volume: current.volume_traded || current.volume || 0,
            bid: current.depth?.buy?.[0]?.price || current.buy_price || 0,
            ask: current.depth?.sell?.[0]?.price || current.sell_price || 0
        };
    }, [quotesData, previousQuote]);

    // Memoized calculation functions to prevent recalculation on every render
    const calculateHedgingMetrics = useCallback((script1, script2) => {
        const script1Quote = getQuoteData(script1?.scriptData?.instrument_token);
        const script2Quote = getQuoteData(script2?.scriptData?.instrument_token);

        // Get price factors (lot size)
        const script1PriceFactor = script1?.scriptData?.priceFactor || 1;
        const script2PriceFactor = script2?.scriptData?.priceFactor || 1;

        // Current market prices (LTP)
        const script1MarketPrice = script1Quote.ltp || 0;
        const script2MarketPrice = script2Quote.ltp || 0;

        // Script prices from the hedging view
        const script1Price = script1?.price || 0;
        const script2Price = script2?.price || 0;

        // Current bid/ask prices
        const script1BidPrice = script1Quote.bid || script1MarketPrice;
        const script2AskPrice = script2Quote.ask || script2MarketPrice;

        // Calculate Margin
        const script1Margin = script1PriceFactor * script1MarketPrice * script1?.quantity || 0;
        const script2Margin = script2PriceFactor * script2MarketPrice * script2?.quantity || 0;
        const totalMargin = script1Margin - script2Margin;

        // Script multipliers
        const multiplier1 = script1?.multiplier === undefined || script1?.multiplier === null ? 1 : script1?.multiplier;
        const multiplier2 = script2?.multiplier === undefined || script2?.multiplier === null ? 1 : script2?.multiplier;

        // Calculate PL
        let script1PL = 0;
        let script2PL = 0;
        if (script1.side === 'BUY') {
            script1PL = (script1BidPrice - script1Price) * (script1PriceFactor * script1?.quantity || 0);
        } else {
            script1PL = (script1Price - script1BidPrice) * (script1PriceFactor * script1?.quantity || 0);
        }

        if (script2.side === 'SELL') {
            script2PL = (script2Price - script2AskPrice) * (script2PriceFactor * script2?.quantity || 0);
        } else {
            script2PL = (script2AskPrice - script2Price) * (script2PriceFactor * script2?.quantity || 0);
        }
        const totalPL = script1PL + script2PL;

        // Calculate P&L point difference
        let diff1 = 0;
        let diff2 = 0;
        if (script1.side === 'BUY') {
            diff1 = (script1BidPrice - script1Price);
        } else {
            diff1 = (script1Price - script1BidPrice);
        }

        if (script2.side === 'SELL') {
            diff2 = (script2Price - script2AskPrice);
        } else {
            diff2 = (script2AskPrice - script2Price);
        }
        const totalDiff = diff1 + diff2;

        return {
            margin: totalMargin,
            pl: totalPL,
            plDiff: totalDiff,
            script1MarketPrice,
            script2MarketPrice,
            multiplier1,
            multiplier2
        };
    }, [getQuoteData]);

    // Delete handlers
    const handleDeleteClick = useCallback((row) => {
        setSelectedRowForDelete(row);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!selectedRowForDelete) return;

        try {
            const result = await dispatch(deleteHedgingView(selectedRowForDelete._id));
            if (result.success) {
                enqueueSnackbar('Hedging view deleted successfully', { variant: 'success' });
                setDeleteDialogOpen(false);
                setSelectedRowForDelete(null);
            } else {
                enqueueSnackbar(result.message || 'Failed to delete hedging view', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Failed to delete hedging view', { variant: 'error' });
        }
    }, [selectedRowForDelete, dispatch, enqueueSnackbar]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteDialogOpen(false);
        setSelectedRowForDelete(null);
    }, []);

    // Toggle expand/collapse for mobile cards
    const toggleCardExpansion = useCallback((rowId) => {
        setExpandedCards(prev => ({
            ...prev,
            [rowId]: !prev[rowId]
        }));
    }, []);

    // Memoized helper functions
    const formatPrice = useCallback((price) => {
        if (!price || Number.isNaN(Number(price))) return '--';
        return parseFloat(price).toFixed(2);
    }, []);

    const getPriceColor = useCallback((change) => {
        if (change > 0) return theme.palette.success.main;
        if (change < 0) return theme.palette.error.main;
        return theme.palette.text.primary;
    }, [theme]);

    const getSideChip = useCallback((side) => (
        <Chip
            label={side}
            size="small"
            variant="filled"
            sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: side === 'BUY'
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                color: 'white',
                borderRadius: '4px',
                minWidth: 45,
            }}
        />
    ), [theme]);

    // Memoized total P&L calculation to prevent recalculation on every render
    const totalPL = useMemo(() => {
        if (!data || data.length === 0) return 0;

        return data.reduce((total, row) => {
            const metrics = calculateHedgingMetrics(row.script1, row.script2);
            return total + metrics.pl;
        }, 0);
    }, [data, calculateHedgingMetrics]);

    // Memoized render functions to prevent unnecessary re-renders
    const renderCollapsedScriptCard = useCallback((script, scriptType) => {
        const instrumentToken = script.scriptData?.instrument_token;
        const quoteData = getQuoteData(instrumentToken);
        const side = script.side;
        const scriptPrice = script.price || 0;
        const priceFactor = script.scriptData?.priceFactor || 1;

        // Get current market price based on side
        let currentMarketPrice = 0;
        if (side === 'BUY' && quoteData.bid) {
            currentMarketPrice = quoteData.bid;
        } else if (side === 'SELL' && quoteData.ask) {
            currentMarketPrice = quoteData.ask;
        } else {
            currentMarketPrice = quoteData.ltp || 0;
        }

        // Calculate P&L
        let pnlDiff = 0;
        let pnl = 0;
        let isProfitable = true;
        if (side === 'BUY') {
            pnlDiff = (currentMarketPrice - scriptPrice);
            pnl = (currentMarketPrice - scriptPrice) * (priceFactor * script.quantity);
            isProfitable = pnl >= 0;
        } else {
            pnlDiff = (scriptPrice - currentMarketPrice);
            pnl = (scriptPrice - currentMarketPrice) * (priceFactor * script.quantity);
            isProfitable = pnl >= 0;
        }

        return (
            <ScriptCard scripttype={scriptType.toLowerCase()}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: 'text.primary'
                        }}
                    >
                        {modifyScriptName(script.scriptData?.tradingsymbol, script.scriptData?.expiry) || 'N/A'}
                    </Typography>
                    {getSideChip(side)}
                </Box>

                <Box sx={{ mt: 1 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            mt: 0.5
                        }}
                    >
                        {formatPrice(scriptPrice)} → <span style={{ color: 'blue' }}>
                            {formatPrice(currentMarketPrice)}
                        </span>
                    </Typography>

                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            display: 'block',
                            mt: 0.5
                        }}
                    >
                        Qty: {script.quantity}
                        • Diff: <span style={{
                            color: pnlDiff > 0 ? '#4caf50' : '#f44336',
                            fontWeight: 600
                        }}>
                            {pnlDiff > 0 ? '+' : ''}{formatPrice(Math.abs(pnlDiff))}
                        </span>
                        • P&L: <span style={{
                            color: isProfitable ? '#4caf50' : '#f44336',
                            fontWeight: 600
                        }}>
                            {isProfitable ? '+' : ''}{formatPrice(Math.abs(pnl))}
                        </span>
                        • {quoteData.changePercent ? `${quoteData.changePercent.toFixed(2)}%` : '0.00%'}
                    </Typography>
                </Box>
            </ScriptCard>
        );
    }, [getQuoteData, formatPrice, getSideChip]);

    // Memoized hedging difference renderer with fixed dimensions
    const renderCollapsedHedgingDifference = useCallback((script1, script2) => {
        const metrics = calculateHedgingMetrics(script1, script2);

        return (
            <AnalysisSection sx={{ py: isMobile ? 1 : 2 }}>
                {!isMobile && (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 1 : 1.5 }}>
                    <Iconify icon="mdi:trending-neutral" sx={{ color: 'info.main', fontSize: 18 }} />
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            color: 'info.main'
                        }}
                    >
                        Hedging Difference
                    </Typography>
                </Box>)}

                {/* Mobile: Stack vertically for better readability */}
                {isMobile ? (
                    <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                Market:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'blue' }}>
                                {formatPrice(Math.abs((metrics.script1MarketPrice * metrics.multiplier1) - (metrics.script2MarketPrice * metrics.multiplier2)))}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                Trade Price:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: metrics.plDiff >= 0 ? 'success.main' : 'error.main'
                                }}
                            >
                                {metrics.plDiff >= 0 ? '+' : '-'}{formatPrice(Math.abs(metrics.plDiff))}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                P&L:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: metrics.pl >= 0 ? 'success.main' : 'error.main'
                                }}
                            >
                                {metrics.pl >= 0 ? '+' : '-'}{formatPrice(Math.abs(metrics.pl))}
                            </Typography>
                        </Box>
                    </Stack>
                ) : (
                    // Desktop: Keep horizontal layout
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block' }}>
                                Market:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '1rem', fontWeight: 600, color: 'blue' }}>
                                {formatPrice(Math.abs((metrics.script1MarketPrice * metrics.multiplier1) - (metrics.script2MarketPrice * metrics.multiplier2)))}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block' }}>
                                Price
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: metrics.plDiff >= 0 ? 'success.main' : 'error.main'
                                }}
                            >
                                {metrics.plDiff >= 0 ? '+' : '-'}{formatPrice(Math.abs(metrics.plDiff))}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block' }}>
                                P&L:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: metrics.pl >= 0 ? 'success.main' : 'error.main'
                                }}
                            >
                                {metrics.pl >= 0 ? '+' : '-'}{formatPrice(Math.abs(metrics.pl))}
                            </Typography>
                        </Box>
                    </Stack>
                )
                }
            </AnalysisSection >
        );
    }, [calculateHedgingMetrics, formatPrice, isMobile]);

    // Render script details section as shown in screenshot  
    const renderScriptDetails = (script, title, bgColor = '#fafbfc') => {
        const scriptPrice = script.price || 0;
        const quantity = script.quantity || 0;
        const totalValue = scriptPrice * quantity;

        return (
            <DetailsSection sx={{ backgroundColor: bgColor }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        mb: 2,
                        color: title.includes('1') ? 'success.main' : 'error.main'
                    }}
                >
                    {title}
                </Typography>

                <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            Price:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {formatPrice(scriptPrice)}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            Quantity:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {quantity}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            Total Volume:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {formatPrice(totalValue)}
                        </Typography>
                    </Box>
                </Stack>
            </DetailsSection>
        );
    };

    // Render hedging analysis as shown in screenshot
    const renderHedgingAnalysis = (script1, script2) => {
        const metrics = calculateHedgingMetrics(script1, script2);

        return (
            <AnalysisSection>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Iconify icon="mdi:trending-neutral" sx={{ color: 'info.main', fontSize: 16 }} />
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: 'info.main'
                        }}
                    >
                        Hedging Analysis
                    </Typography>
                </Box>

                <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            Market Diff:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: 'text.primary'
                            }}
                        >
                            {formatPrice(Math.abs(metrics.script1MarketPrice - metrics.script2MarketPrice))}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            Margin:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: metrics.margin >= 0 ? 'success.main' : 'error.main'
                            }}
                        >
                            {formatPrice(Math.abs(metrics.margin))} {metrics.margin >= 0 ? '' : ''}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            Net P&L:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: metrics.pl >= 0 ? 'success.main' : 'error.main'
                            }}
                        >
                            {metrics.pl >= 0 ? '+' : '-'}{formatPrice(Math.abs(metrics.pl))}
                        </Typography>
                    </Box>
                </Stack>
            </AnalysisSection>
        );
    };

    // Memoized mobile card renderer
    const renderMobileCard = useCallback((row) => {
        const isExpanded = expandedCards[row._id];

        return (
            <Paper key={row._id} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                {/* Header with expand/collapse toggle */}
                <Box sx={{
                    p: 1.5,
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: 'text.primary'
                        }}
                    >
                        {modifyScriptName(row.script1?.scriptData?.tradingsymbol, row.script1?.scriptData?.expiry)} vs {modifyScriptName(row.script2?.scriptData?.tradingsymbol, row.script2?.scriptData?.expiry)}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => toggleCardExpansion(row._id)}
                        sx={{
                            backgroundColor: 'white',
                            boxShadow: 1,
                            '&:hover': { backgroundColor: 'grey.100' }
                        }}
                    >
                        <Iconify
                            icon={isExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"}
                            width={18}
                        />
                    </IconButton>
                </Box>

                {/* Always visible: Hedging Difference Card (Compact) */}
                <Box sx={{ p: 1.5, pb: isExpanded ? 1.5 : 2 }}>
                    {/* <Typography
                        variant="caption"
                        sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'info.main',
                            mb: 1,
                            display: 'block'
                        }}
                    >
                        Hedging Difference
                    </Typography> */}
                    {renderCollapsedHedgingDifference(row.script1, row.script2)}
                </Box>

                {/* Expandable Content */}
                {isExpanded && (
                    <Box sx={{ p: 1.5, pt: 0, borderTop: '1px solid #f0f0f0' }}>
                        {/* Script Cards - Stacked vertically for better readability */}
                        <Stack spacing={2}>
                            {/* Script 1 Card */}
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        color: 'success.main',
                                        mb: 1,
                                        display: 'block'
                                    }}
                                >
                                    Script 1
                                </Typography>
                                {renderCollapsedScriptCard(row.script1, 'buy')}
                            </Box>

                            {/* Script 2 Card */}
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        color: 'error.main',
                                        mb: 1,
                                        display: 'block'
                                    }}
                                >
                                    Script 2
                                </Typography>
                                {renderCollapsedScriptCard(row.script2, 'sell')}
                            </Box>
                        </Stack>

                        {/* Action Buttons */}
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Stack direction="row" spacing={1}>
                                {/* <Tooltip title="Edit">
                                    <IconButton size="small" color="primary">
                                        <Iconify icon="eva:edit-fill" width={18} />
                                    </IconButton>
                                </Tooltip> */}
                                <Tooltip title="Delete">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteClick(row);
                                        }}
                                    >
                                        <Iconify icon="eva:trash-2-fill" width={18} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>
                    </Box>
                )}
            </Paper>
        );
    }, [expandedCards, renderCollapsedHedgingDifference, renderCollapsedScriptCard, toggleCardExpansion, handleDeleteClick]);

    // General P&L display - keeping original styling
    const renderGeneralPL = useCallback(() => {
        const isProfitable = totalPL >= 0;

        return (
            <Box sx={{
                mb: 1,
                p: 1,
                backgroundColor: alpha(isProfitable ? theme.palette.success.main : theme.palette.error.main, 0.1),
                borderRadius: 1,
                border: `1px solid ${alpha(isProfitable ? theme.palette.success.main : theme.palette.error.main, 0.2)}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: 'text.primary'
                    }}
                >
                    General P&L:
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: isProfitable ? 'success.main' : 'error.main'
                    }}
                >
                    {isProfitable ? '+' : '-'} {formatPrice(Math.abs(totalPL))}
                </Typography>
            </Box>
        );
    }, [totalPL, theme, formatPrice]);

    // Memoized desktop table renderer
    const renderDesktopTable = useCallback(() => (
        <CompactCard>
            <Scrollbar>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell className="header">Script 1</StyledTableCell>
                                <StyledTableCell className="header">Script 2</StyledTableCell>
                                <StyledTableCell className="header">Hedging Difference</StyledTableCell>
                                <StyledTableCell className="header">Actions</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row) => (
                                <TableRow key={row._id}>
                                    {/* Script 1 Column */}
                                    <StyledTableCell>
                                        {renderCollapsedScriptCard(row.script1, 'buy')}
                                    </StyledTableCell>

                                    {/* Script 2 Column */}
                                    <StyledTableCell>
                                        {renderCollapsedScriptCard(row.script2, 'sell')}
                                    </StyledTableCell>

                                    {/* Hedging Difference Column */}
                                    <StyledTableCell>
                                        {renderCollapsedHedgingDifference(row.script1, row.script2)}
                                    </StyledTableCell>

                                    {/* Actions Column */}
                                    <StyledTableCell align="center">
                                        <Stack direction="column" spacing={1} alignItems="center">
                                            {/* <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Add edit functionality here
                                                    }}
                                                >
                                                    <Iconify icon="eva:edit-fill" width={18} />
                                                </IconButton>
                                            </Tooltip> */}
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(row);
                                                    }}
                                                >
                                                    <Iconify icon="eva:trash-2-fill" width={18} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </StyledTableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Scrollbar>
        </CompactCard>
    ), [data, renderCollapsedScriptCard, renderCollapsedHedgingDifference, handleDeleteClick]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography>Loading hedging views...</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* General P&L Summary */}
            {data && data.length > 0 && renderGeneralPL()}

            {/* Data Display */}
            {isMobile ? (
                <Box sx={{ px: 1 }}>
                    {data.map(renderMobileCard)}
                </Box>
            ) : (
                renderDesktopTable()
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2,
                    px: isMobile ? 1 : 0,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 2 : 0
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                            Rows per page:
                        </Typography>
                        <FormControl size="small">
                            <Select
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                sx={{ minWidth: 80, height: 32, fontSize: '0.8rem' }}
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem', ml: 2 }}>
                            {pagination.pagingCounter}-{Math.min(pagination.pagingCounter + pageSize - 1, pagination.totalDocs)} of {pagination.totalDocs}
                        </Typography>
                    </Box>

                    <Pagination
                        count={pagination.totalPages}
                        page={currentPage}
                        onChange={(_, page) => onPageChange(page)}
                        size={isMobile ? "medium" : "small"}
                        showFirstButton
                        showLastButton
                        sx={{
                            '& .MuiPaginationItem-root': {
                                fontSize: '0.8rem',
                                minWidth: 32,
                                height: 32,
                            }
                        }}
                    />
                </Box>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        Confirm Delete
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete this hedging view?
                    </Typography>
                    {selectedRowForDelete && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Script 1:</strong> {selectedRowForDelete.script1?.scriptData?.tradingsymbol} ({selectedRowForDelete.script1?.side})
                            </Typography>
                            <Typography variant="body2">
                                <strong>Script 2:</strong> {selectedRowForDelete.script2?.scriptData?.tradingsymbol} ({selectedRowForDelete.script2?.side})
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        startIcon={<Iconify icon="eva:trash-2-fill" />}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});

export default HedgingViewTable;
